import {TXHR} from '../../assets/constants/constansts.type';

export const ORDER_STATUS_CANCELED = 3;

export type TEticketSeatPos = {
    x: number;
    y: number;
    seat?: string;
    row?: string;
};

export type TEticketSeatMap = {
    width?: string;
    height?: string;
    stage?: {path?: string};
    area?: Record<string, {path?: string; title?: string; mine?: boolean; seats?: TEticketSeatPos[]}>;
};

export type TEticketEvent = {
    image?: string;
    imageColors?: string[];
    title: string;
    date?: string;
    timeOpen?: string;
    timeStart?: string;
    timeEnd?: string;
    /* IANA timezone of the event (e.g. 'Asia/Jerusalem') — times are formatted in it */
    timeZone?: string;
    location?: string;
    address?: string;
    seatMap?: TEticketSeatMap;
};

export type TEticketSeat = {
    seat?: string | number; // seat.x_title (כסא)
    row?: string | number; // seat.y_title (שורה)
    section?: string | number; // area.title (גוש)
    gate?: string | number; // אין מקור ב-DB עדיין (שער)
    aid?: number | string; // area.aid — לזיהוי הגוש של הכרטיס במפה
};

export type TEticketTicket = {
    title?: string;
    color?: string;
    seat?: TEticketSeat;
    inviterName?: string;
    oid: number;
    barcode: string;
    tid: number;
    qid: number;
    additionalInfo?: string;
    customFields?: {
        title: string;
        value: string;
    }[];
    scanned?: boolean;
    canceled?: boolean;
    scanTs?: number;
    /* Present only on a subscription ticket — the ticket itself is the series (tid === plan.planTid) */
    subscription?: TEticketTicketSubscription;
};

export type TEticketTransaction = {
    price: string;
    discount?: string;
    couponDiscount?: string;
    itemsCount?: number;
};

export type TEticketProducer = {
    packageName?: string;
    email?: string;
    phone?: string;
    billingId?: string;
    logo?: string;
};

export type TEticketLiveLink = {
    type: string;
    title: string;
    image?: string;
    url: string;
};

export type TEticketLive = {
    logo?: string;
    links: TEticketLiveLink[];
};

export type TEticketOrder = {
    oid: number;
    nameFirst?: string;
    nameLast?: string;
    email?: string;
    phone?: string;
    lang?: string;
    oCurrency?: string;
    ref?: string;
    source?: string;
    payMethod?: string;
    created?: string;
    newsletter?: number;
    status?: number; // ORDER_STATUS_CANCELED (3) = עסקה מבוטלת
};

export type TEticketLinks = {
    eventPage?: string;
    print?: string;
    cancel?: string;
    personalArea?: string;
    receipt?: string;
};

export type TCancelTicket = {
    allowed?: boolean;
    reason?: string;
};

/* Same values as the planType of react-share SubscriptionCard / SeriesTypeBadge */
export type TEticketSubscriptionPlanType = 'open' | 'closed' | 'mixed';
export type TEticketSubscriptionStatus = 'active' | 'expired' | 'redeemed' | 'canceled';
/* Derived from the facts the API returns about the event — see getSubscriptionEventState */
export type TEticketSubscriptionEventState = 'active' | 'soldOut' | 'inactive' | 'passed';

/* An event of the series — a row in the "tickets to redeem" list */
export type TEticketSubscriptionEvent = {
    planTid?: number;
    eid: number;
    title: string;
    /* unix timestamps; null while the event has no schedule yet */
    timeStart?: number | null;
    timeEnd?: number | null;
    location?: string | null;
    /* 1 = the show is live and published, 0 = it belongs to the series but is not open for purchase.
       Series events are listed either way — one that is not open shows as unavailable. */
    active?: number;
    image?: string;
    /* No stock left on any of the show's ticket types */
    soldOut?: boolean;
    /* This subscription already took a ticket for that show */
    redeemed?: boolean;
    /* The sale page of that show, built by the API from the event's domain and name.
       The subscription itself is not in the address — the sale page reads the member's
       subscriptions off the account they are logged in with. */
    url?: string;
};

/* Response of GET /eTicket/:token/subscription/:qid/events */
export type TEticketSubscriptionEventsPage = {
    linkedEvents: TEticketSubscriptionEvent[];
    total: number;
    limit: number;
    offset: number;
};

/* The tickets bought with the subscription in one order — a row of "my tickets", already grouped
   and counted by the API. The eTicket page it links to is the one of the ticket the qid belongs to. */
export type TEticketSubscriptionTicket = {
    qid: number;
    oid: number;
    eid: number;
    subscriptionQid?: number;
    /* How many tickets of that show were taken in the order */
    ticketsCount?: number;
    title: string;
    /* unix timestamp; null while the show has no schedule yet */
    timeStart?: number | null;
    location?: string | null;
    image?: string;
    url?: string;
};

/* Response of GET /eTicket/:token/subscription/:qid/tickets */
export type TEticketSubscriptionTicketsPage = {
    tickets: TEticketSubscriptionTicket[];
    total: number;
    limit: number;
    offset: number;
};

/* The series itself — the plan the ticket was sold on */
export type TEticketSubscriptionPlan = {
    planTid: number;
    planType?: TEticketSubscriptionPlanType;
    /* Credits of a single subscription (the ticket's total is credits.total) */
    credits?: number;
    realizations?: number;
    /* End of the season's usage period — the subscription expires with it */
    usageDeadline?: string | number;
    /* "About the series" */
    description?: string;
    /* Id of the series cover on the plan — the API resolves it into `image` below */
    coverImageId?: number;
    /* Address of the series cover, resolved from coverImageId */
    image?: string;
};

export type TEticketSubscriptionCredits = {
    total: number;
    used: number;
    remaining: number;
};

/* A show tickets were redeemed for, with how many of them — already grouped by the API and summed
   over every active subscription of the series in the order, so it is complete on the first response */
export type TEticketSubscriptionRedeemed = {
    eid: number;
    title: string;
    timeStart?: number | null;
    location?: string | null;
    ticketsCount: number;
};

export type TEticketTicketSubscription = {
    plan: TEticketSubscriptionPlan;
    /* How many subscriptions were bought of that series — a further purchase adds to the same ticket */
    subscribersCount?: number;
    canceledCount?: number;
    credits: TEticketSubscriptionCredits;
    /* Title of the price the subscription was sold on (single, couple, family...) — free text the producer wrote */
    priceTitle?: string;
    /* The venue of the series, set by the API only when all of its published events share one */
    location?: string;
    /* Its full address (city included) when the events resolve to one — the navigation target */
    address?: string;
    redeemed?: TEticketSubscriptionRedeemed[];
    events?: TEticketSubscriptionEvent[];
    tickets?: TEticketSubscriptionTicket[];
};

export type TEticketData = {
    lang: string;
    event: TEticketEvent;
    tickets: TEticketTicket[];
    transaction?: TEticketTransaction;
    producer?: TEticketProducer;
    order?: TEticketOrder;
    links?: TEticketLinks;
    live?: TEticketLive;
    walletAllowed?: boolean;
    cancel?: TCancelTicket;
    cancelAllowed?: boolean;
};

export type TEticketScanStatus = {
    scanned: 0 | 1;
    scanTs?: number;
};

export type TEticket = {
    data: TEticketData | null;
    oid: number | null;
    error: string | null;
    getETicketXHR: TXHR;
    getETicketScanStatusXHR: TXHR;
};
